import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { AnswerResult, AttemptCompletionResult, QuizView, ReaderStatsView } from '@/types/api';

/**
 * Public (reader) quiz API: fetch a quiz by parent, run an attempt
 * (start → per-answer → complete), and query per-reader stats.
 *
 * Mutations are all on this service because the attempt lifecycle is
 * strictly a reader action — not something an admin does on behalf of
 * someone else. Admin authoring lives on `admin-quizzes.service.ts`.
 */
export const quizzesService = {
  async getForBook(bookId: string): Promise<QuizView | null> {
    return apiRequest<QuizView | null>(apiRoutes.quizzes.forBook(bookId), {
      method: 'GET',
    });
  },

  async getForArticle(slug: string): Promise<QuizView | null> {
    return apiRequest<QuizView | null>(apiRoutes.quizzes.forArticle(slug), {
      method: 'GET',
    });
  },

  async getById(id: string): Promise<QuizView> {
    return apiRequest<QuizView>(apiRoutes.quizzes.one(id), { method: 'GET' });
  },

  async startAttempt(quizId: string, readerId: number | string): Promise<{ attemptId: string }> {
    return apiRequest<{ attemptId: string }>(apiRoutes.quizzes.startAttempt(quizId), {
      method: 'POST',
      body: { readerId: Number(readerId) },
    });
  },

  async recordAnswer(
    attemptId: string,
    payload: { readerId: number | string; questionId: string; optionId: string },
  ): Promise<AnswerResult> {
    return apiRequest<AnswerResult>(apiRoutes.quizzes.recordAnswer(attemptId), {
      method: 'POST',
      body: {
        readerId: Number(payload.readerId),
        questionId: payload.questionId,
        optionId: payload.optionId,
      },
    });
  },

  async completeAttempt(
    attemptId: string,
    readerId: number | string,
  ): Promise<AttemptCompletionResult> {
    return apiRequest<AttemptCompletionResult>(apiRoutes.quizzes.completeAttempt(attemptId), {
      method: 'POST',
      body: { readerId: Number(readerId) },
    });
  },

  async getReaderStats(readerId: number | string): Promise<ReaderStatsView> {
    return apiRequest<ReaderStatsView>(apiRoutes.quizzes.readerStats(readerId), {
      method: 'GET',
    });
  },
};
